import * as React from "react";
import { AlertTriangle, Inbox, LoaderCircle } from "lucide-react";
import { cn } from "./cn.js";

export function LoadingState({ label = "Wird geladen ..." }: { label?: string }) {
  return (
    <div className="flex items-center justify-center gap-2 py-10 text-sm text-slate-500 dark:text-slate-400">
      <LoaderCircle className="h-4 w-4 animate-spin" aria-hidden />
      <span>{label}</span>
    </div>
  );
}

export function Skeleton({ className }: { className?: string }) {
  return <div className={cn("animate-pulse rounded-md bg-slate-200 dark:bg-slate-800", className)} />;
}

export function SkeletonRows({ rows = 5, columns = 4 }: { rows?: number; columns?: number }) {
  return (
    <div className="flex flex-col gap-2">
      {Array.from({ length: rows }).map((_, r) => (
        <div key={r} className="flex gap-2">
          {Array.from({ length: columns }).map((_, c) => (
            <Skeleton key={c} className="h-6 flex-1" />
          ))}
        </div>
      ))}
    </div>
  );
}

export function EmptyState({
  title,
  description,
  action,
  icon,
}: {
  title: string;
  description?: string;
  action?: React.ReactNode;
  icon?: React.ReactNode;
}) {
  return (
    <div className="flex flex-col items-center justify-center gap-2 py-12 text-center">
      <div className="mb-1 rounded-full bg-slate-100 p-3 text-slate-400 dark:bg-slate-800 dark:text-slate-500">
        {icon ?? <Inbox className="h-6 w-6" />}
      </div>
      <p className="text-sm font-medium text-ink dark:text-slate-100">{title}</p>
      {description && <p className="max-w-sm text-sm text-slate-500 dark:text-slate-400">{description}</p>}
      {action}
    </div>
  );
}

export function ErrorState({
  title = "Es ist ein Fehler aufgetreten",
  description,
  correlationId,
  action,
}: {
  title?: string;
  description?: string;
  correlationId?: string;
  action?: React.ReactNode;
}) {
  return (
    <div className="flex flex-col items-center justify-center gap-2 py-12 text-center" role="alert">
      <div className="mb-1 rounded-full bg-red-50 p-3 text-red-500 dark:bg-red-900/40 dark:text-red-300">
        <AlertTriangle className="h-6 w-6" />
      </div>
      <p className="text-sm font-medium text-ink dark:text-slate-100">{title}</p>
      {description && <p className="max-w-sm text-sm text-slate-500 dark:text-slate-400">{description}</p>}
      {correlationId && (
        <p className="font-mono text-xs text-slate-400 dark:text-slate-500">Korrelations-ID: {correlationId}</p>
      )}
      {action}
    </div>
  );
}
